import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, ShieldCheck } from 'lucide-react'
import { useT } from '@/i18n'
import { fetchSuspiciousStaff, type SuspiciousStaffRow } from '@/lib/adapters'
import { cn } from '@/lib/cn'

function fmtTime(ts: string | number): string {
  const n = typeof ts === 'number' ? ts : Number(ts)
  if (!Number.isFinite(n) || n <= 0) return '—'
  try {
    return new Date(n).toLocaleString()
  } catch {
    return '—'
  }
}

const WINDOWS = [7, 30, 90] as const

/**
 * A18.05 — Shubhali xodim faoliyati. Audit jurnalini xodim bo'yicha N kunda
 * agregatlaydi va DESTRUKTIV amallar (ban/delete/block/revoke/role/…) hajmini
 * ko'rsatadi. Xodim STATISTIK-outlier (>= o'rtacha + 2σ, >=5) yoki mutlaq-yuqori
 * (>=20) bo'lsa «shubhali» deb belgilanadi. Haqiqiy heuristika — soxta ball emas.
 * SecurityScreen tab-i (o'z PageHeader'i yo'q). Read-only, AUDIT_VIEW (tab-shell gate).
 */
export function SuspiciousStaff() {
  const t = useT()
  const [days, setDays] = useState<number>(7)
  const [rows, setRows] = useState<SuspiciousStaffRow[] | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback((d: number) => {
    setLoading(true)
    fetchSuspiciousStaff(d)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load(days)
  }, [days, load])

  const flagged = (rows ?? []).filter((r) => r.suspicious).length

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
        <div className="text-xs text-tertiary">
          {t.suspStaff.note}
          {rows !== null && (
            <span className={cn('ml-2 font-medium', flagged > 0 ? 'text-danger' : 'text-success')}>
              {t.suspStaff.flaggedCount(flagged)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              className={cn(
                'rounded-md border px-2.5 py-1 text-xs',
                days === w ? 'border-accent bg-accent text-white' : 'border-border-default bg-surface-2 text-secondary hover:text-primary',
              )}
            >
              {t.suspStaff.days(w)}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {rows === null || loading ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <ShieldCheck size={24} className="mx-auto mb-2 opacity-60" />
            {t.states.emptyTitle}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border-default text-left text-xs text-tertiary">
                  <th className="px-2 py-1.5 font-medium">{t.col.staff}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t.suspStaff.destructive}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t.suspStaff.total}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t.suspStaff.targets}</th>
                  <th className="px-2 py-1.5 font-medium">{t.col.time}</th>
                  <th className="px-2 py-1.5 font-medium">{t.col.status}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.admin} className={cn('border-b border-border-subtle', r.suspicious && 'bg-danger-subtle')}>
                    <td className="px-2 py-1.5 font-medium text-primary">{r.admin}</td>
                    <td className={cn('px-2 py-1.5 text-right font-mono tabular', r.destructive > 0 ? 'text-danger' : 'text-tertiary')}>
                      {r.destructive.toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono tabular text-secondary">{r.total.toLocaleString()}</td>
                    <td className="px-2 py-1.5 text-right font-mono tabular text-tertiary">{r.targets.toLocaleString()}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-tertiary">{fmtTime(r.last_at)}</td>
                    <td className="px-2 py-1.5">
                      {r.suspicious ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-danger px-2 py-0.5 text-xs text-white">
                          <AlertTriangle size={11} />
                          {r.reason === 'high_absolute' ? t.suspStaff.reasonHigh : t.suspStaff.reasonOutlier}
                        </span>
                      ) : (
                        <span className="text-xs text-tertiary">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
